import React from "react";
import Image from "next/image";
import { Box, SimpleGrid, type SpaceProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { type FeaturedCustomerSlug, type Customer } from "lib/sanity";
import { Rule } from "ui/rule";
import { Statistic, type StatisticProps } from "ui/text";
import { handleClientError } from "lib/errors";
import { Section } from "components/section";
import { getCustomerLogoStyles, getCustomerLogoUrl } from "utils";

export type CustomerStatsProps = {
  /**
   * The color theme to use for text and logo colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The set of customer stats to display. Designed for displaying three stats.
   *
   * The following customers have stats available to display:
   * - `auto-trader`
   * - `gorgias`
   * - `lucidchart`
   * - `minno`
   * - `petsmart`
   * - `wmg`
   * - `the-zebra`
   */
  customers: Array<Pick<Customer, "slug" | "name" | "logoUrl" | "logoMonoUrl">>;
} & SpaceProps;

/**
 * All stats are keyed by the `slug` value of the customer.
 */
const stats: Record<FeaturedCustomerSlug, Pick<StatisticProps, "title" | "description">> = {
  "auto-trader": {
    title: "20%",
    description: (
      <>
        increase
        <br />
        in CTR
      </>
    ),
  },
  gorgias: {
    title: "60%",
    description: (
      <>
        customer
        <br />
        acquisition lift
      </>
    ),
  },
  lucidchart: {
    title: "52%",
    description: (
      <>
        increase
        <br />
        in ROAS
      </>
    ),
  },
  minno: { title: "1 day", description: "migration" },
  petsmart: {
    title: "200%",
    description: (
      <>
        boost in
        <br />
        engagement
      </>
    ),
  },
  wmg: {
    title: "1000+",
    description: (
      <>
        audiences <br />
        managed
      </>
    ),
  },
  "the-zebra": {
    title: "170%",
    description: (
      <>
        match rate
        <br />
        increase
      </>
    ),
  },
};

export function CustomerStats({ theme = "light", customers, ...spaceProps }: CustomerStatsProps) {
  return (
    <Section my="huger" {...spaceProps}>
      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap={{ base: "biggest", md: "smaller", lg: "small" }}
        justifyItems="center"
      >
        {customers.map((customer, idx) => {
          const stat = stats[customer.slug as FeaturedCustomerSlug];

          if (!stat) {
            handleClientError(new Error(`Could not find stat to display for customer with slug '${customer.slug}'.`));
          }

          return (
            <Box
              // Index in key only needed to prevent potential rendering issues in Storybook
              // eslint-disable-next-line react/no-array-index-key
              key={`${customer.slug}-${idx}`}
              w="100%"
              maxW={{
                base: "100%",
                sm: "350px", // Only cap width from sm to md breakpoint so the single column doesn't look as silly there
                md: "100%",
              }}
            >
              <Statistic theme={theme} title={stat.title} description={stat.description} />

              <Rule theme={theme} pb="smaller" />

              <Box width="170px" height="30px" maxW="100%" maxH="100%" pos="relative" {...getCustomerLogoStyles(theme)}>
                <Image
                  fill
                  sizes="100%"
                  src={`${getCustomerLogoUrl(customer, theme)}?w=400`}
                  alt={`${customer.name}.`}
                  style={{ objectFit: "contain", objectPosition: "left" }}
                />
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>
    </Section>
  );
}
